
import React, { useState } from 'react';
import { cn } from "@/lib/utils";
import { ActionCardProps, ActionCardDataItem } from '@/types/action-card';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, ChevronDown, ChevronUp, CircleCheck, ExternalLink, Search } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

export const ListCard = ({ data, className }: ActionCardProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter items based on search query
  const filteredItems = data.items?.filter(item => 
    !searchQuery || 
    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ 
        type: "spring",
        stiffness: 500,
        damping: 30
      }}
      className={cn(
        "relative",
        "shadow-lg hover:shadow-xl transition-all duration-300",
        className
      )}
    >
      <Card className={cn(
        "backdrop-blur-sm bg-white/10 dark:bg-gray-900/20",
        "border border-white/20 dark:border-gray-800/30",
        "bg-gradient-to-br from-primary/10 to-primary/5",
        "p-4 overflow-hidden"
      )}>
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-white/10 dark:border-gray-800/50">
          <div className="flex items-center gap-3">
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="relative flex h-8 w-8 items-center justify-center rounded-lg bg-white/20 dark:bg-gray-800/30"
            >
              <Search className="h-4 w-4 text-primary" />
            </motion.div>
            <div className="space-y-0.5">
              <h3 className="text-lg font-semibold tracking-tight">{data.title}</h3>
              <p className="text-sm text-muted-foreground">{data.summary}</p>
            </div>
          </div>
          
          {data.isCollapsible && (
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/10 dark:hover:bg-gray-800/50 transition-colors"
              aria-label={isCollapsed ? "Expand card" : "Collapse card"}
            >
              {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
          )}
        </div>

        {/* Content */}
        <AnimatePresence>
          {!isCollapsed && data.items && data.items.length > 0 && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              {/* Search input - only show if we have more than 3 items */}
              {data.items.length > 3 && (
                <div className="pt-4 pb-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground/70" />
                    <input
                      type="text"
                      placeholder="Search items..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full rounded-md bg-white/10 dark:bg-gray-800/30 border border-white/10 dark:border-gray-800/30 py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                </div>
              )}

              {/* List items */}
              <div className="py-2 space-y-2">
                {filteredItems && filteredItems.length > 0 ? (
                  filteredItems.map((item, index) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ 
                        opacity: 1, 
                        y: 0,
                        transition: { delay: index * 0.05 }
                      }}
                      className={cn(
                        "group flex items-center justify-between p-3 rounded-lg",
                        "bg-white/5 dark:bg-gray-800/20 hover:bg-white/10 dark:hover:bg-gray-800/30",
                        "border border-white/5 dark:border-gray-800/20",
                        "transition-all duration-200"
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{item.title}</div>
                        {item.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>
                        )}
                        {item.meta && Object.keys(item.meta).length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {Object.entries(item.meta).map(([key, value], idx) => (
                              <Badge key={idx} variant="outline" className="text-[10px] bg-white/5">
                                {key}: {String(value)}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {item.url && (
                        <a 
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 p-1.5 rounded-full opacity-0 group-hover:opacity-100 bg-primary/10 hover:bg-primary/20 transition-all duration-200"
                        >
                          <ExternalLink className="h-4 w-4 text-primary" />
                        </a>
                      )}
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    {searchQuery ? "No results match your search" : "No items to display"}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Timestamp */}
        <div className="pt-2 text-[10px] text-muted-foreground">
          {data.timestamp}
        </div>
      </Card>
    </motion.div>
  );
};
